import {
    Chip,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    Stack,
} from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import { IOverlappingClauseInfo } from "../../types";

interface IProps {
    /**
     * Whether the popover is open
     */
    open: boolean;
    /**
     * The anchor element for the popover
     */
    anchorEl: HTMLElement | null;
    /**
     * The overlapping clauses to display
     */
    overlappingClauses: IOverlappingClauseInfo[];
    /**
     * Function to close the popover
     */
    handleClosePopover: () => void;
    /**
     * Function to handle the selection of a clause
     * @param scopeClauseId - The scope clause id to select (unique per topic)
     */
    handleClauseSelect: (scopeClauseId: number) => void;
}

/**
 * A popover that displays the overlapping clauses when clicking on overlapping highlights.
 */
export const OverlappingClausesPopover = ({
    open,
    anchorEl,
    overlappingClauses,
    handleClosePopover,
    handleClauseSelect,
}: IProps) => {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: 280,
                        maxWidth: 400,
                        elevation: 3,
                    },
                },
            }}
        >
            <List dense>
                {overlappingClauses.map((clauseInfo, index) => (
                    <Fragment key={clauseInfo.scopeClauseId}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() =>
                                    handleClauseSelect(clauseInfo.scopeClauseId)
                                }
                            >
                                <ListItemText
                                    primary={clauseInfo.clauseTitle}
                                    secondary={
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap={"wrap"}
                                        >
                                            {/* Policy Type Label */}
                                            <Chip
                                                label={
                                                    clauseInfo.policyType
                                                        .short_name
                                                }
                                                size={"small"}
                                            />

                                            {/* Clause Types */}
                                            {clauseInfo.clauseTypes.map(
                                                ({ name }, index) => (
                                                    <Chip
                                                        key={`${name}-${index}`}
                                                        label={name}
                                                        size={"small"}
                                                        color={"tertiary"}
                                                    />
                                                )
                                            )}

                                            {/* Topic Name (for V2 scopes) */}
                                            {clauseInfo.topicName && (
                                                <Chip
                                                    label={clauseInfo.topicName}
                                                    size={"small"}
                                                    color="secondary"
                                                />
                                            )}
                                        </Stack>
                                    }
                                    slotProps={{
                                        primary: {
                                            color: "primary",
                                            gutterBottom: true,
                                            sx: {
                                                textDecoration: "underline",
                                            },
                                            fontWeight: "bold",
                                            variant: "body2",
                                        },
                                        secondary: {
                                            component: "div",
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        {index !== overlappingClauses.length - 1 && (
                            <Divider component="li" />
                        )}
                    </Fragment>
                ))}
            </List>
        </Popover>
    );
};
